/**
 * <s-gallery-thumbs> — thumbnail strip for the product gallery.
 *
 * Clicking a thumbnail scrolls the gallery to that slide; scrolling or swiping
 * the gallery highlights the matching thumbnail via aria-current.
 *
 * Required markup:
 *   s-product-image-zoom [data-product-gallery] — horizontal scroll-snap gallery, one full-width child per media.
 *   [data-thumb-index] — thumbnail buttons inside this element, zero-based.
 */
class SGalleryThumbs extends HTMLElement {
  #ac;
  #gallery = null;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#gallery = this.closest('s-product-image-zoom')?.querySelector('[data-product-gallery]') ?? null;
    if (!this.#gallery) return;

    this.addEventListener('click', this.#handleClick, opts);
    this.#gallery.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
    this.#gallery = null;
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-thumb-index]');
    if (!button || !this.contains(button) || !this.#gallery) return;

    const index = Number(button.dataset.thumbIndex);
    const direction = getComputedStyle(this.#gallery).direction === 'rtl' ? -1 : 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#gallery.scrollTo({
      left: index * this.#gallery.clientWidth * direction,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.#setActive(index);
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (!this.#gallery || this.#gallery.clientWidth === 0) return;
      const index = Math.round(Math.abs(this.#gallery.scrollLeft) / this.#gallery.clientWidth);
      this.#setActive(index);
    });
  };

  #setActive(index) {
    for (const button of this.querySelectorAll('[data-thumb-index]')) {
      button.setAttribute('aria-current', String(Number(button.dataset.thumbIndex) === index));
    }
  }
}

if (!customElements.get('s-gallery-thumbs')) {
  customElements.define('s-gallery-thumbs', SGalleryThumbs);
}
